import React, { useCallback } from 'react';
import styles from './tv-guide.module.scss';
import { useNavigate } from 'react-router-dom';
import testIds from '@smart-tv/e2e/shared/variables';
import Dialog, { DialogItem } from '@smart-tv/components/dialog';
import Checkmark from '@smart-tv/components/icons/checkmark';
import useQuery from '@smart-tv/hooks/use-query';
import addDays from '@smart-tv/utils/date/add-days';
import eachDayOfInterval from '@smart-tv/utils/date/each-day-of-interval';
import getStartOfDay from '@smart-tv/utils/date/get-start-of-day';
import isSameDay from '@smart-tv/utils/date/is-same-day';
import subtractDays from '@smart-tv/utils/date/subtract-days';

import formatDate from '@smart-tv/utils/date/format-date';
import getCalendarDateText from '@smart-tv/utils/date/get-calendar-date-text';
import addQueryParams from '@smart-tv/utils/routing/add-query-params';
import removeQueryParams from '@smart-tv/utils/routing/remove-query-params';

import TvGuideContext from '@smart-tv/pages/tv-guide/utils/tv-guide-context';
import {
  DATEPICKER_SCROLL_HEIGHT,
  DATE_FOCUS_KEY
} from '@smart-tv/pages/tv-guide/utils/tv-guide-helpers';
import TvGuideDayViewContext from '../utils/tv-guide-day-view-context';
import { SidebarContent, SidebarHeader, Sidebar } from '@smart-tv/components/dialog';
import NetworkErrorDialog from '@smart-tv/components/error-dialog/network-error-dialog';
import useNetwork from '@smart-tv/hooks/use-network/use-network';
import DialogContext from '@smart-tv/contexts/dialog-context/dialog-context';

const getCalendarDates = () => {
  const currentDate = getStartOfDay(new Date());
  const startDate = subtractDays(currentDate, 7);
  const endDate = addDays(currentDate, 7);

  return eachDayOfInterval(startDate, endDate);
};

interface QueryParams {
  date?: string;
}

export const TvGuideDialogCalendar: React.FC = () => {
  const { calendarDialog, setCalendarDialog, setDateChanged } = React.useContext(TvGuideContext);
  const { setActiveCenter, setActiveTVGuideFocusKey } = React.useContext(TvGuideDayViewContext);
  const { handleSetDialogFocusKey } = React.useContext(DialogContext);

  const navigate = useNavigate();

  const [calendarDates] = React.useState(getCalendarDates);
  const { date = new Date() } = useQuery<QueryParams>();

  const scrollingRef = React.useRef<HTMLDivElement>(null);

  const { checkAndUpdateNetwork, isOffline, setIsOffline } = useNetwork();

  const [lastClickedDate, setLastClickedDate] = React.useState<Date>(
    date ? new Date(date) : new Date()
  );

  const calendarDateFocused = useCallback(
    ({ node }: { node: HTMLElement }) => {
      if (scrollingRef.current !== null) {
        // Use the node's own offsetTop (stable) rather than the scroll-relative y from the layout,
        // which would compound drift across successive focus moves.
        scrollingRef.current.scrollTo({
          top: node.offsetTop - DATEPICKER_SCROLL_HEIGHT,
          behavior: 'auto'
        });
      }
    },
    [scrollingRef]
  );

  const handleCloseCalendarDialog = useCallback(() => {
    setCalendarDialog(false);
    //set focus key to DATE_FOCUS_KEY using handleSetDialogFocusKey from dialog context otherwise it will be overwritten
    handleSetDialogFocusKey(DATE_FOCUS_KEY);
  }, [setCalendarDialog, handleSetDialogFocusKey]);

  const handleDatePressed = useCallback(
    async (date: Date) => {
      setIsOffline(false);
      const hasNetwork = await checkAndUpdateNetwork();
      if (hasNetwork) {
        navigate(
          isSameDay(new Date(), date)
            ? removeQueryParams('date')
            : addQueryParams({ date: formatDate(date, 'M/d/y') })
        );

        setDateChanged(true);
        //reset focus
        setActiveCenter(undefined);
        setActiveTVGuideFocusKey(undefined);

        handleCloseCalendarDialog();
      }
    },

    [navigate, setActiveCenter, setActiveTVGuideFocusKey, handleCloseCalendarDialog, setDateChanged]
  );

  const handleAndSaveDatePressed = useCallback(
    async (date: Date) => {
      setLastClickedDate(date);
      await handleDatePressed(date);
    },
    [handleDatePressed, setLastClickedDate]
  );

  const handleCloseNetworkErrorDialogAction = useCallback(() => {
    setIsOffline(false);
  }, [setIsOffline]);

  if (!calendarDialog) return null;

  return (
    <Dialog
      open={calendarDialog}
      onClose={handleCloseCalendarDialog}
      isSidebar={true}
      dialogContainerProps={{ 'data-testid': testIds.tvGuide.filter.calendar.dialog }}
    >
      <Sidebar>
        <SidebarHeader variant="h2" data-testid={testIds.tvGuide.filter.calendar.title}>
          Vælg dato
        </SidebarHeader>
        <SidebarContent ref={scrollingRef} data-testid={testIds.tvGuide.filter.calendar.list}>
          {calendarDates.map((calendarDate, index) => {
            const isSelected = isSameDay(calendarDate, new Date(date));
            return (
              <DialogItem
                key={`calendar-date-${index}`}
                dataTestId={testIds.tvGuide.filter.calendar.item}
                isFocused={isSelected}
                selected={isSelected}
                onEnterPress={() => handleAndSaveDatePressed(calendarDate)}
                onFocus={calendarDateFocused}
                itemProps={{ 'data-testid': testIds.tvGuide.filter.calendar.item }}
              >
                <div className={styles['tv-guide-dialog__calendar-date-row']}>
                  <span>{getCalendarDateText(calendarDate)}</span>
                  {isSelected && <Checkmark size='xl' />}
                </div>
              </DialogItem>
            );
          })}
        </SidebarContent>
        {isOffline && (
          <NetworkErrorDialog
            onTryAgain={() => handleDatePressed(lastClickedDate)}
            onClose={() => handleCloseNetworkErrorDialogAction()}
          />
        )}
      </Sidebar>
    </Dialog>
  );
};

export default React.memo(TvGuideDialogCalendar);
